"use client";

import { useState, useEffect } from "react";

import type { ConsentChoice } from "@/@types/cookie-consent";
import {
	getCookieConsent,
	setCookieConsent,
	hasAcceptedCookies,
	initializeTracking,
} from "@/lib/utils/cookie-consent";
import { detectIOSWebview } from "@/lib/utils/tracking";

function syncIOSConsent(): ConsentChoice {
	const authorized = window.appTrackingAuthorized;

	if (authorized === undefined) return getCookieConsent();

	const choice: ConsentChoice = authorized ? "accepted" : "declined";
	const current = getCookieConsent();

	if (current !== choice) {
		setCookieConsent(choice);
	}

	return choice;
}

export function useCookieConsent() {
	const [consent, setConsent] = useState<ConsentChoice>(null);
	const [showBanner, setShowBanner] = useState(false);

	useEffect(() => {
		if (detectIOSWebview()) {
			// iOS handles consent natively via ATT — never show the web banner.
			// Sync the ATT result to the cookieConsent cookie so the rest of the
			// app can read a single source of truth.
			setShowBanner(false);
			setConsent(syncIOSConsent());

			const handleTrackingChange = () => {
				setConsent(syncIOSConsent());
			};

			window.addEventListener(
				"trackingAuthorizationChanged",
				handleTrackingChange,
			);

			return () => {
				window.removeEventListener(
					"trackingAuthorizationChanged",
					handleTrackingChange,
				);
			};
		}

		// Check consent status on mount
		const currentConsent = getCookieConsent();
		setConsent(currentConsent);
		setShowBanner(currentConsent === null);

		// Initialize tracking if already accepted
		if (currentConsent === "accepted") {
			initializeTracking();
		}
	}, []);

	const acceptCookies = () => {
		setCookieConsent("accepted");
		setConsent("accepted");
		setShowBanner(false);
		initializeTracking();
	};

	const declineCookies = () => {
		setCookieConsent("declined");
		setConsent("declined");
		setShowBanner(false);
	};

	return {
		consent,
		showBanner,
		acceptCookies,
		declineCookies,
		hasAccepted: hasAcceptedCookies(),
	};
}
